/**
 * Hook for managing word dictionary entries.
 * Supports global, case, and file scopes.
 */

import { useState, useEffect, useCallback } from 'react';
import { dictionaryApi, apiHelpers } from '../lib/api';
import type { WordDictionaryEntry } from '../types';

interface UseDictionaryReturn {
  entries: WordDictionaryEntry[];
  isLoading: boolean;
  error: string | null;
  hasChanges: boolean;
  addEntry: (data: {
    word: string;
    action: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }) => Promise<WordDictionaryEntry | null>;
  updateEntry: (id: string, data: {
    word?: string;
    action?: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }) => Promise<WordDictionaryEntry | null>;
  deleteEntry: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useDictionary(
  scopeType: 'global' | 'case' | 'file',
  scopeId?: string
): UseDictionaryReturn {
  const [entries, setEntries] = useState<WordDictionaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dictionaryApi.getEntries({
        scope_type: scopeType,
        scope_id: scopeId,
        limit: 1000,
      });
      setEntries(response.entries);
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to fetch dictionary entries:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [scopeType, scopeId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const addEntry = useCallback(async (data: {
    word: string;
    action: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }): Promise<WordDictionaryEntry | null> => {
    try {
      const newEntry = await dictionaryApi.createEntry({
        scope_type: scopeType,
        scope_id: scopeId,
        word: data.word,
        action: data.action,
        match_type: data.matchType ?? 'exact',
        reason: data.reason,
      });
      setEntries((prev) => [newEntry, ...prev]);
      setHasChanges(true);
      return newEntry;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to create dictionary entry:', errorMessage);
      return null;
    }
  }, [scopeType, scopeId]);

  const updateEntry = useCallback(async (id: string, data: {
    word?: string;
    action?: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }): Promise<WordDictionaryEntry | null> => {
    try {
      const updated = await dictionaryApi.updateEntry(id, {
        word: data.word,
        action: data.action,
        match_type: data.matchType,
        reason: data.reason,
      });
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      return updated;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to update dictionary entry:', errorMessage);
      return null;
    }
  }, []);

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    try {
      await dictionaryApi.deleteEntry(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setHasChanges(true);
      return true;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to delete dictionary entry:', errorMessage);
      return false;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchEntries();
  }, [fetchEntries]);

  return { entries, isLoading, error, hasChanges, addEntry, updateEntry, deleteEntry, refresh };
}
